import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import {
  default as toastBase,
  ToastContent,
  ToastOptions,
} from '#/components/toasts/toast';
import { toastTrade as baseToastTrade } from '#/components/toasts/toastTrade';
import {
  toastQuoteCreated,
  ToastableQuote,
} from '#/components/toasts/toastQuoteCreated';
import { GrfqTrade } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { Trade as DrfqTrade } from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import {
  useFormattedDrfqMakerPrice,
  useFormattedGrfqMakerPrice,
} from '@paradigm/features/src/unified-rfqs/ui/hooks/useFormattedPrice';
import { Order } from '@paradigm/features/src/unified-rfqs/entities/drfq/orders';
import ToastRfqOrderCanceled from '#/components/UnifiedRfqs/drfq/ToastRfqOrderCanceled';
import { BaseWarningToast } from '@paradigm/design-system/src/components/Toast';
import WarningIcon from '@paradigm/design-system/src/assets/WarningIcon';
import { findBaseProductCode } from '@paradigm/features/src/products/domain';
import {
  primaryProductCode,
  strategyProductCodes,
} from '@paradigm/features/src/unified-rfqs/utils/legs';
import ToastRfqClosed from './drfq/ToastRfqClosed';

export const BASE_TOAST_CONTAINER_ID = 'unified-rfqs-base-toasts';
export const TRADE_TOAST_CONTAINER_ID = 'unified-rfqs-trade-toasts';

export const TOAST_TIMEOUT_LONG = 10_000;
export const TOAST_TIMEOUT_SHORT = 3_000;

export const toast = (content: ToastContent, options: ToastOptions = {}) =>
  toastBase(content, {
    containerId: BASE_TOAST_CONTAINER_ID,
    autoClose: TOAST_TIMEOUT_LONG,
    ...options,
  });

export const toastWarning = (
  message: string,
  errorDetails?: string,
  errorType?: 'trade' | 'other',
) =>
  toastBase(
    <BaseWarningToast
      errorMessage={message}
      errorDetails={errorDetails}
      css={{ margin: '4px 0px' }}
    />,
    {
      styles: { alignItems: 'unset' },
      icon: <WarningIcon width={20} height={20} css={{ marginTop: 8 }} />,
      containerId:
        errorType === 'trade'
          ? TRADE_TOAST_CONTAINER_ID
          : BASE_TOAST_CONTAINER_ID,
      autoClose:
        errorType === 'trade' ? TOAST_TIMEOUT_LONG : TOAST_TIMEOUT_SHORT,
    },
  );

export const toastTrade = (trade: GrfqTrade | DrfqTrade) => {
  const productCode =
    trade.rfqType === 'grfq'
      ? findBaseProductCode(trade.product_codes)
      : primaryProductCode(trade.legs);
  // GRFQ can never be multi-product (order book stays single-product by
  // backend design), so productCodes is only meaningful for DRFQ. Derived
  // from the strategy (non-hedge) legs only — trade.legs may include a
  // hedge leg on a different product, which must not count as a second
  // "product" for this purpose.
  const productCodes =
    trade.rfqType === 'grfq' ? undefined : strategyProductCodes(trade.legs);

  baseToastTrade(
    {
      ...trade,
      productCode,
      productCodes,
      price: <ToastTradePrice trade={trade} />,
    },
    {
      containerId: TRADE_TOAST_CONTAINER_ID,
      autoClose: TOAST_TIMEOUT_LONG,
      pauseOnFocusLoss: false,
    },
  );
};

// Fires when the backend emits a SUBMITTED progress tick — only for
// async-settlement venues (Paradex) where the trade has non-trivial
// wall-clock time between submit and terminal outcome. A light-weight
// info toast is enough to show movement; the terminal FILLED/REJECTED
// toast follows when the poll completes.
export const toastTradeSubmitted = (trade: DrfqTrade) => {
  toastBase(
    <span>
      Trade submitted — awaiting {trade.venue} settlement.
      <br />
      {trade.description}
    </span>,
    {
      containerId: TRADE_TOAST_CONTAINER_ID,
      autoClose: TOAST_TIMEOUT_SHORT,
      pauseOnFocusLoss: false,
    },
  );
};

export const toastDrfqClosed = (drfq: Drfq) =>
  toastBase(<ToastRfqClosed drfq={drfq} />, {
    containerId: BASE_TOAST_CONTAINER_ID,
    styles: {
      content: {
        margin: 'auto 14px auto 0',
      },
    },
    icon: false,
  });

export const toastQuote = (quote: ToastableQuote, description: string) =>
  toastQuoteCreated(quote, description, {
    containerId: BASE_TOAST_CONTAINER_ID,
    autoClose: TOAST_TIMEOUT_LONG,
    pauseOnFocusLoss: false,
  });

export const toastDrfqOrderCanceled = (order: Order) => {
  // Only notify Taker agress orders (one click trade)
  if (order.role !== 'TAKER' && order.time_in_force !== 'FILL_OR_KILL') return;

  toastBase(<ToastRfqOrderCanceled order={order} />, {
    containerId: BASE_TOAST_CONTAINER_ID,
  });
};

function ToastTradePrice({ trade }: { trade: GrfqTrade | DrfqTrade }) {
  switch (trade.rfqType) {
    case 'drfq':
      return <DrfqToastTradePrice trade={trade} />;
    case 'grfq':
      return <GrfqToastTradePrice trade={trade} />;
    default:
      return null;
    /** no default */
  }
}

function DrfqToastTradePrice({ trade }: { trade: DrfqTrade }) {
  const formattedPrice = useFormattedDrfqMakerPrice({
    price: trade.price,
    side: trade.side,
    drfqInfo: {
      ...trade,
      product_codes: trade.legs.map((leg) => leg.product_code),
    },
  });

  return <span>{formattedPrice}</span>;
}

function GrfqToastTradePrice({ trade }: { trade: GrfqTrade }) {
  const formattedPrice = useFormattedGrfqMakerPrice({
    price: trade.price,
    side: trade.action,
    productCode: trade.product_codes[0],
    description: trade.description,
    isTaker: trade.desk_role === 'Taker',
  });

  return <span>{formattedPrice}</span>;
}
